'use client';

import EyeOffSvg from '@/assets/svgs/eye-off.svg';
import EyeOnSvg from '@/assets/svgs/eye-on.svg';
import UsersFilledSvg from '@/assets/svgs/main/users-filled.svg';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { useAssignUserToAccount, useCreateUser } from '@/containers/Users/api';
import { CreateUserPayload } from '@/containers/Users/types';
import {
  selectCurrentAccount,
  selectCurrentAccountId,
  selectSubAccountsByRoot,
} from '@/lib/redux/app.selectors';
import { Account, SubAccount } from '@/lib/redux/app.slice';
import { AssignmentList } from '@/shared/components/AssignmentList';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';
import { UserTimezoneSelect } from '@/shared/components/UserTimezoneSelect';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modals } from '../all-modals';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { CreateAccountUserModalParams, ModalProps } from '../types';
import useModals from '../useModals';
import AccountItemView from './AccountItemView';

interface CreateAccountUserModalProps extends ModalProps {
  params?: CreateAccountUserModalParams;
}

const modalName = Modals.CreateAccountUserModal;

const USER_TYPE_OPTIONS = [
  { value: 'USER', label: 'User' },
  { value: 'ADMIN-USER', label: 'Admin User' },
] as const;

const CreateAccountUserModal = ({
  className,
  variant = 'default',
}: CreateAccountUserModalProps) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const { onClose, isModalOpen } = useModals({
    modalName,
  });

  const toast = useToast();
  const currentAccountId = useSelector(selectCurrentAccountId);
  const currentAccount = useSelector(selectCurrentAccount);
  const subAccountsByRoot = useSelector(selectSubAccountsByRoot);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [userType, setUserType] = useState<'USER' | 'ADMIN-USER'>('USER');
  const [timezone, setTimezone] = useState('US/Eastern');
  const [assignedAccountIds, setAssignedAccountIds] = useState<string[]>([]);
  const [accountSearchText, setAccountSearchText] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createUserMutation = useCreateUser();
  const assignUserToAccountMutation = useAssignUserToAccount();

  // Determine if we should show account assignment UI
  // Show if the current account has sub-accounts, regardless of whether it's root or sub
  const shouldShowAccountAssignment = useMemo(() => {
    if (!currentAccount) return false;
    const subs = subAccountsByRoot[currentAccount.account_id] || [];
    return subs.length > 0;
  }, [currentAccount, subAccountsByRoot]);

  // Build list of assignable accounts
  const assignableAccounts = useMemo<Account[]>(() => {
    if (!shouldShowAccountAssignment || !currentAccount) return [];

    const accounts: Account[] = [currentAccount];
    const subs = subAccountsByRoot[currentAccount.account_id] || [];
    subs.forEach((sub: SubAccount) => {
      accounts.push({
        account_id: sub.account_id,
        account_name: sub.account_name,
        account_type: 'sub',
        parent_account_id: currentAccount.account_id,
      });
    });

    return accounts;
  }, [shouldShowAccountAssignment, currentAccount, subAccountsByRoot]);

  // Filter accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!accountSearchText) return assignableAccounts;
    return assignableAccounts.filter((account) =>
      account.account_name.toLowerCase().includes(accountSearchText.toLowerCase())
    );
  }, [assignableAccounts, accountSearchText]);

  // Separate assigned and available accounts
  const assignedAccounts = useMemo(() => {
    return filteredAccounts.filter((account) =>
      assignedAccountIds.includes(account.account_id)
    );
  }, [filteredAccounts, assignedAccountIds]);

  const availableAccounts = useMemo(() => {
    return filteredAccounts.filter(
      (account) => !assignedAccountIds.includes(account.account_id)
    );
  }, [filteredAccounts, assignedAccountIds]);

  // Initialize with current account assigned
  useEffect(() => {
    if (
      shouldShowAccountAssignment &&
      currentAccountId &&
      assignedAccountIds.length === 0
    ) {
      setAssignedAccountIds([currentAccountId]);
    }
  }, [shouldShowAccountAssignment, currentAccountId, assignedAccountIds.length]);

  const handleAssignAccount = (account: Account) => {
    setAssignedAccountIds((prev) => [...prev, account.account_id]);
  };

  const handleUnassignAccount = (account: Account) => {
    setAssignedAccountIds((prev) => prev.filter((id) => id !== account.account_id));
  };

  const handleAssignAllAccounts = () => {
    const allIds = assignableAccounts.map((acc) => acc.account_id);
    setAssignedAccountIds(allIds);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.showError('Please fix the errors in the form');
      return;
    }

    // Determine which accounts to assign
    let accountsToAssign: string[];
    if (shouldShowAccountAssignment) {
      if (assignedAccountIds.length === 0) {
        toast.showError('Please assign at least one account');
        return;
      }
      accountsToAssign = assignedAccountIds;
    } else {
      // Auto-assign current account
      accountsToAssign = currentAccountId ? [currentAccountId] : [];
    }

    const payload: CreateUserPayload = {
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      password: password.trim(),
      user_type: userType,
      local_timezone: timezone,
      root_accounts: accountsToAssign,
      identities: [],
      user_hash: null,
      user_id: null,
      username: null,
    };

    try {
      const response = await createUserMutation.mutateAsync(payload);
      const newUserId = String(response.data.Data.user_id);

      // Handle account assignments if there are any
      if (shouldShowAccountAssignment && assignedAccountIds.length > 0) {
        // Iterate through all assigned accounts and assign user to each
        await Promise.all(
          assignedAccountIds.map((accountId) =>
            assignUserToAccountMutation.mutateAsync({
              userId: newUserId,
              payload: { account_id: accountId },
            })
          )
        );
      }

      toast.showSuccess('User created successfully');
      onClose();
      // Reset form
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
      setUserType('USER');
      setTimezone('US/Eastern');
      setAssignedAccountIds([]);
      setErrors({});
    } catch (error) {
      console.error('Failed to create user:', error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to create user';
      toast.showError(errorMessage);
    }
  };

  const unmount = () => {};

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => unmount, []);

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={true}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <form onSubmit={handleSubmit} className="flex h-full flex-col overflow-hidden">
          {/* Stick to top */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<UsersFilledSvg className="h-5 w-5" />}
            title="Create New User"
          />

          {/* Take up remaining space and enable inside-scrolling */}
          <ModalBody ref={modalBodyRef} className="px-5 !py-0">
            {/* Spacing for consistency */}
            <div className="h-6" />

            {/* Form Fields */}
            <div className="flex flex-col gap-4 pb-6">
              {/* Row 1: First Name & Last Name */}
              <div className="grid grid-cols-2 gap-4">
                {/* First Name */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">First Name</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={classNames('input input-bordered w-full', {
                      'input-error': errors.firstName,
                    })}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.firstName}
                      </span>
                    </label>
                  )}
                </div>

                {/* Last Name */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Last Name</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={classNames('input input-bordered w-full', {
                      'input-error': errors.lastName,
                    })}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.lastName}</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Row 2: Email & Password */}
              <div className="grid grid-cols-2 gap-4">
                {/* Email */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Email</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={classNames('input input-bordered w-full', {
                      'input-error': errors.email,
                    })}
                    placeholder="Enter email"
                  />
                  {errors.email && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.email}</span>
                    </label>
                  )}
                </div>

                {/* Password */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Password</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={classNames('input input-bordered w-full pr-10', {
                        'input-error': errors.password,
                      })}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                      {showPassword ? (
                        <EyeOffSvg className="h-5 w-5" />
                      ) : (
                        <EyeOnSvg className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.password}</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Row 3: User Type & Timezone */}
              <div className="grid grid-cols-2 gap-4">
                {/* User Type */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">User Type</span>
                  </label>
                  <select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value as 'USER' | 'ADMIN-USER')}
                    className="select select-bordered w-full">
                    {USER_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Timezone */}
                <UserTimezoneSelect value={timezone} onChange={setTimezone} />
              </div>

              {/* Account Assignment (Conditional) */}
              {shouldShowAccountAssignment && (
                <div className="mt-4">
                  <label className="label">
                    <span className="label-text">Account Assignment</span>
                  </label>

                  {/* Search */}
                  <div className="mb-4">
                    <SearchBar
                      search={accountSearchText}
                      setSearch={setAccountSearchText}
                      placeholder="Search accounts..."
                      debounceMs={300}
                      ariaLabel="Search accounts"
                      className="w-full"
                    />
                  </div>

                  {/* Assignment List */}
                  <AssignmentList
                    assignedItems={assignedAccounts}
                    availableItems={availableAccounts}
                    LineItemView={AccountItemView}
                    onAssign={handleAssignAccount}
                    onUnassign={handleUnassignAccount}
                    onAssignAll={handleAssignAllAccounts}
                    isLoading={false}
                    searchText={accountSearchText}
                    emptyMessage="No accounts found"
                    getItemKey={(account) => account.account_id}
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-6 flex items-center justify-end gap-3">
                <button type="button" onClick={onClose} className="btn btn-outline">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={
                    createUserMutation.isPending || assignUserToAccountMutation.isPending
                  }>
                  {createUserMutation.isPending ||
                  assignUserToAccountMutation.isPending ? (
                    <span className="loading loading-spinner"></span>
                  ) : null}
                  Create User
                </button>
              </div>
            </div>
          </ModalBody>
        </form>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default CreateAccountUserModal;
